import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Popover,
  Badge,
  Skeleton
} from "@mui/material";
import { Download, Search, ShieldOutlined, TimelineOutlined, FilterList } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { useTheme, alpha } from "@mui/material/styles";
import Layout from "../layout/layout";
import { api } from "../services/api";

const getActionChipColor = (action = "") => {
  switch (action.toLowerCase().trim()) {
    case "create":
      return "primary";
    case "delete":
      return "error";
    case "update":
      return "warning";
    case "login":
      return "secondary";
    case "logout":
      return "info";
    case "other":
      return "default";
    case "deploy":
      return "success";
    default:
      return "default";
  }
};

const StatCard = ({ eyebrow, value, caption, icon, accent, bgColor }) => (
  <Card 
    elevation={0}
    sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      p: 1.5, 
      borderRadius: 3,
      backgroundColor: bgColor || '#ffffff', 
      border: '1px solid rgba(0,0,0,0.05)',
      height: '100%',
    }}
  >
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        p: 2, 
        mr: 3, 
        borderRadius: 2, 
        backgroundColor: alpha(accent, 0.2),
        color: accent
      }}
    >
      {icon}
    </Box>

    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography 
        variant="overline" 
        color="text.secondary" 
        sx={{ lineHeight: 1.2, mb: 0.5, letterSpacing: 1 }}
      >
        {eyebrow}
      </Typography>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5, color: 'text.primary' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    </Box>
  </Card>
);

const AuditPage = () => {
  const theme = useTheme();
  
  // Unified pagination model (0-indexed for DataGrid)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  // Data states
  const [auditData, setAuditData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [uniqueUsers, setUniqueUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Popover state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const isFilterApplied = Boolean(debouncedSearch.trim() !== "" || actionFilter !== "all");

  const userId = sessionStorage.getItem('userId');

 const pageTitle = useMemo(() => {
  try {
    const menus = JSON.parse(sessionStorage.getItem('userMenus') || '[]');
    const matchedMenu = menus.find(
      (m) => m.path?.toLowerCase() === '/audit'
    );
    return matchedMenu?.label ?? null;
  } catch {
    return null;
  }
}, []);

  // Debounce search typing and reset page to 0
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data on page, pageSize, or filter changes
  useEffect(() => {
    const fetchAuditData = async () => {
      if (!userId) {
        setIsLoading(false);
        setAuditData([]);
        return;
      }

      setIsLoading(true);
      try {
        const queryParams = {
          pageNumber: paginationModel.page + 1, // Swagger API is 1-indexed
          pageSize: paginationModel.pageSize,
        };

        if (debouncedSearch.trim()) {
          queryParams.searchTerm = debouncedSearch.trim();
        }

        if (actionFilter !== "all") {
          queryParams.actionType = actionFilter.toUpperCase();
        }

        const response = await api.get(`/Report/auditReport/${userId}`, {
          params: queryParams,
          headers: {
            accept: "application/json",
            "X-CORRELATION-ID": "ccac4ba8-8c6e-4ab7-b9cd-a29a596f4dee",
          },
        });

        const data = response.data;
        const items = Array.isArray(data?.items) ? data.items : [];
        setAuditData(items);
        setTotalCount(data?.totalCount ?? items.length);
        setUniqueUsers(data?.uniqueUserCount ?? new Set(items.map((i) => i.userMailId)).size);
      } catch (error) {
        console.error("Error fetching audit data:", error);
        setAuditData([]);
        setTotalCount(0);
        setUniqueUsers(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuditData();
  }, [userId, paginationModel, debouncedSearch, actionFilter]);

  const columns = [
    {
      field: "userMailId",
      headerName: "User",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => (
        <Typography sx={{ color: "text.primary", fontSize: 12, py: 1.5 }}>
          {params.value || params.row.userName || "-"}
        </Typography>
      ),
    },
    {
      field: "organizationName",
      headerName: "Organization",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography sx={{ color: "text.primary", fontSize: 12, py: 1.5 }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "actionType",
      headerName: "Action",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        const actionValue = params.value || "";
        const isOther = actionValue.toLowerCase().trim() === "other";
        
        return (
          <Chip
            label={actionValue || "-"}
            color={isOther ? "default" : getActionChipColor(actionValue)}
            size="small"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "capitalize",
              ...(isOther ? {
                bgcolor: theme.palette.mode === "dark" ? "#475569" : "#e2e8f0", 
                color: theme.palette.mode === "dark" ? "#f8fafc" : "#0f172a",   
              } : {}),
            }}
          />
        );
      },
    },
    {
      field: "resourceName",
      headerName: "Resource Name",
      flex: 1.25,
      minWidth: 180,
      renderCell: (params) => (
        <Typography sx={{ color: "text.primary", fontSize: 12, py: 1.5 }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "resourceType",
      headerName: "Resource Type",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography sx={{ color: "text.primary", fontSize: 12, py: 1.5 }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "activeAccountId",
      headerName: "Active Account",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Typography sx={{ color: "text.primary", fontSize: 12, py: 1.5 }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "sessionId",
      headerName: "Session Id",
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <Typography sx={{ color: "text.secondary", fontSize: 12, py: 1.5 }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "details",
      headerName: "Details",
      flex: 1.8,
      minWidth: 260,
      renderCell: (params) => (
        <Typography sx={{ color: "text.secondary", fontSize: 12, py: 1.5 }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "changedDate",
      headerName: "Timestamp",
      minWidth: 190,
      renderCell: (params) => {
        if (!params.value) return <Typography sx={{ fontSize: 12 }}>-</Typography>;
        try {
          return (
            <Typography sx={{ color: "text.primary", fontSize: 12, py: 1.5 }}>
              {format(new Date(params.value), "PPpp")}
            </Typography>
          );
        } catch {
          return <Typography sx={{ fontSize: 12 }}>{params.value}</Typography>;
        }
      },
    },
  ];

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(auditData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Audit Data");
    XLSX.writeFile(workbook, "audit_data.xlsx");
  };

  return (
    <Layout pageType="sideBar">
      <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="title" fontWeight="bold">
              {pageTitle && (
  <Typography variant="title" sx={{ m: 0 }}>
    {pageTitle}
</Typography>
)}

            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 620 }}>
              Monitor and track all system activities and user actions
            </Typography>
          </Box>
          
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant={isFilterApplied ? "contained" : "outlined"}
              color={isFilterApplied ? "primary" : "inherit"}
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              sx={{ borderRadius: 3, px: 2.5, py: 1.2, borderColor: "rgba(0,0,0,0.2)" }}
              startIcon={
                <Badge color="error" variant="dot" invisible={!isFilterApplied}>
                  <FilterList />
                </Badge>
              }
            >
              Filter Activity
            </Button>

            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={exportToExcel}
              sx={{ borderRadius: 3, px: 2.5, py: 1.2 }}
            >
              Export Audit
            </Button>
          </Box>
        </Box>

        {/* Filter Popover */}
        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ 
            sx: { borderRadius: 3, mt: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } 
          }}
        >
          <Box sx={{ p: 3, width: { xs: 280, sm: 360 } }}>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}>
                Filter Activity
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Search by user, resource, action, session, or details.
              </Typography>
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Search audit activity..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "text.secondary", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select
                value={actionFilter}
                label="Action"
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="all">All Actions</MenuItem>
                <MenuItem value="login">Login</MenuItem>
                <MenuItem value="create">Create</MenuItem>
                <MenuItem value="update">Update</MenuItem>
                <MenuItem value="delete">Delete</MenuItem>
                <MenuItem value="view">View</MenuItem>
                <MenuItem value="deploy">Deploy</MenuItem>
                <MenuItem value="configure">Configure</MenuItem>
                <MenuItem value="logout">Logout</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
              <Button 
                variant="text" 
                color="inherit"
                disabled={!isFilterApplied}
                onClick={() => {
                  setSearchTerm("");
                  setDebouncedSearch("");
                  setActionFilter("all");
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              >
                Clear
              </Button>
              <Button 
                variant="contained" 
                onClick={() => setFilterAnchorEl(null)}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Summary Stat Cards */}
        <Grid container spacing={3} sx={{ mb: 2, flexShrink: 0 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard
              eyebrow="TOTAL EVENTS"
              value={isLoading ? <Skeleton animation="wave" width={80} height={40} /> : totalCount}
              caption="Total recorded audit events"
              icon={<TimelineOutlined fontSize="medium" />}
              accent={theme.palette.primary.main}
              bgColor={theme.palette.mode === 'dark' ? '#1e293b' : '#f0f4f8'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard
              eyebrow="UNIQUE USERS"
              value={isLoading ? <Skeleton animation="wave" width={80} height={40} /> : uniqueUsers}
              caption="Distinct users recorded in audit logs"
              icon={<ShieldOutlined fontSize="medium" />}
              accent={theme.palette.success.main}
              bgColor={theme.palette.mode === 'dark' ? '#0f3a1d' : '#edf7ed'}
            />
          </Grid>
        </Grid>

        {/* DataGrid */}
        <Box sx={{ flex: 1, height: 'calc(100vh - 290px)', minHeight: 380, width: '100%' }}>
          <DataGrid
            rows={auditData}
            columns={columns}
            rowCount={totalCount}
            loading={isLoading}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
            hideFooterSelectedRowCount
            getRowId={(row) => row.auditId ?? row.id ?? `${row.changedDate}-${Math.random()}`}
            autoHeight={false}
            sx={{
              border: "none",
              p: 1,
              borderRadius: 2,
              bgcolor: (t) => t.palette.mode === "dark" ? "background.paper" : "#ffffff",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                color: "text.primary",
                borderBottom: "none",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold !important",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              },
              "& .MuiDataGrid-cell": {
                color: "text.primary",
                borderBottom: "none !important",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "#fafafa",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "transparent" : "#ffffff",
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08) !important" : "#e8f0fe !important",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": { 
                outline: "none" 
              },
            }}
          />
        </Box>
      </Box>
    </Layout>
  );
};

export default AuditPage;